/**
 * Social Element Colors Store — user-customisable colors per element type
 *
 * @module core/modules/social/store/element-colors-store
 *
 * @description
 * Zustand store with localStorage persistence. Keeps only user overrides;
 * falls back to SOCIAL_ELEMENT_COLORS defaults for untouched entries.
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { SOCIAL_ELEMENT_COLORS, type SocialElementId } from '../constants/element-colors';

interface SocialElementColorsState {
  /** Only colours the user has explicitly changed */
  overrides: Partial<Record<SocialElementId, string>>;
}

interface SocialElementColorsActions {
  /** Return override if set, otherwise default */
  getColor: (id: SocialElementId) => string;
  /** Set a user override */
  setColor: (id: SocialElementId, hex: string) => void;
  /** Remove a single override (revert to default) */
  resetColor: (id: SocialElementId) => void;
  /** Remove all overrides */
  resetAll: () => void;
}

export type SocialElementColorsStore = SocialElementColorsState & SocialElementColorsActions;

export const useSocialElementColorsStore = create<SocialElementColorsStore>()(
  devtools(
    persist(
      (set, get) => ({
        overrides: {},

        getColor(id: SocialElementId): string {
          return get().overrides[id] ?? SOCIAL_ELEMENT_COLORS[id];
        },

        setColor(id: SocialElementId, hex: string): void {
          set(
            (s) => ({ overrides: { ...s.overrides, [id]: hex } }),
            false,
            'setColor',
          );
        },

        resetColor(id: SocialElementId): void {
          set(
            (s) => {
              const next = { ...s.overrides };
              delete next[id];
              return { overrides: next };
            },
            false,
            'resetColor',
          );
        },

        resetAll(): void {
          set({ overrides: {} }, false, 'resetAll');
        },
      }),
      { name: 'social-element-colors' },
    ),
    { name: 'SocialElementColors' },
  ),
);
